import { useCallback, useEffect, useRef, useState } from 'react'
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { useCurrentSelf } from '../../../hooks/auth/useCurrentSelf'
import { clockReadScope, clockDownloadScope } from './queryScope'
import { getClockStatus, punchClock, type ClockKind, type ClockStatus } from '../../../api/time_clock/requests'
import {
  cleanUpOfflinePunches, enqueuePunch, isConnectionFailure, readQueue, refuse, updateQueue, useOfflineQueue, withQueuedPunches, type QueuedPunch,
} from './offlineQueue'

export function useClockIdentity() {
  const { data } = useCurrentSelf()
  return clockReadScope(data)
}
/** Storage ownership is the actual employee ID, never the clock query/cache scope. */
export function useClockEmployeeId() {
  const { data } = useCurrentSelf()
  return data?.employeeId || ''
}
/** Server time minus this device's time at the last status, so a queued punch can reveal a clock change. */
const serverOffsets = new Map<string, number>()

/**
 * The clock's status, with punches still waiting on this device applied. Without a connection the
 * last known status stays usable (`offline`); any other failure still hides it, as before.
 */
export function useClockStatus(enabled = true) {
  const identity = useClockIdentity()
  const employeeId = useClockEmployeeId()
  const queue = useOfflineQueue(employeeId)
  const query = useQuery({
    queryKey: ['time-clock', identity, 'status'],
    queryFn: async () => {
      const status = await getClockStatus()
      const expectedOrg = identity.split(':')[1]
      if (status.owner?.employee !== employeeId || status.owner?.organization !== expectedOrg) throw new Error('Clock workspace changed')
      serverOffsets.set(`${employeeId}:${expectedOrg}`, Math.round((Date.parse(status.server_now) - Date.now()) / 1000))
      return status
    },
    enabled: enabled && !!identity,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    retry: 1,
    staleTime: 5000,
  })
  const offline = query.isError && !!query.data && isConnectionFailure(query.error)
  return {
    ...query,
    data: query.data ? withQueuedPunches(query.data, queue) : query.data,
    isError: query.isError && !offline,
    offline,
  }
}
export function useClockMutation<T, R = unknown>(
  mutationFn: (value: T) => Promise<R>,
  success: string | ((result: R) => string) = 'saved',
  networkMode: 'online' | 'always' = 'online',
  isCurrent?: () => boolean,
) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation()
  return useMutation({
    mutationKey: ['time-clock-write'],
    networkMode,
    mutationFn,
    onSuccess: (result) => {
      if (isCurrent && !isCurrent()) return
      const key = typeof success === 'function' ? success(result) : success
      toast({ title: t(`timeClock.${key}`), status: 'success', duration: 2500, isClosable: true })
    },
    onError: (error) => {
      if (isCurrent && !isCurrent()) return
      const response = isAxiosError(error) ? error.response : undefined
      // A plain refusal (no role or feature here) has no code; say so instead of a bare "failed".
      const code = response?.data?.code ?? (response?.status === 403 ? 'forbidden' : undefined)
      toast({
        title: t('timeClock.failed'),
        description: code ? t(`timeClock.errorCodes.${code}`, { defaultValue: '' }) : undefined,
        status: 'error',
        duration: 7000,
        isClosable: true,
      })
    },
    onSettled: () => {
      if (isCurrent && !isCurrent()) return
      void queryClient.invalidateQueries({ queryKey: ['time-clock'] })
      void queryClient.invalidateQueries({ queryKey: ['notifications'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-notifications'] })
    },
  })
}

/** Immediate file/print ownership check; independent of when React commits its next render. */
export function useClockFileScope() {
  const { data: self } = useCurrentSelf()
  const client = useQueryClient()
  const scope = clockDownloadScope(self)
  const mounted = useRef(true)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
  const isCurrent = useCallback(() => mounted.current && !!scope && clockDownloadScope(client.getQueryData(['current-self'])) === scope, [client, scope])
  return { scope, isCurrent }
}

/** Only file/print callers opt in; durable punches and write mutations are unchanged. */
export function useClockDownload<T>(download: (value: T, isCurrent: () => boolean) => Promise<void>) {
  const { isCurrent } = useClockFileScope()
  return useClockMutation(async (value: T) => {
    if (!isCurrent()) throw new Error('Clock workspace changed')
    await download(value, isCurrent)
    if (!isCurrent()) throw new Error('Clock workspace changed')
  }, 'downloaded', 'online', isCurrent)
}
export function usePunch(status?: ClockStatus) {
  const busy = useIsMutating({ mutationKey: ['time-clock-write'] }) > 0
  const identity = useClockEmployeeId()
  const { data: self } = useCurrentSelf()
  const mutation = useClockMutation(async (kind: ClockKind): Promise<'recorded' | 'savedOffline'> => {
    const owner = status?.owner
    if (!owner) throw new Error('offline_unavailable')
    if (owner.employee !== identity || owner.organization !== self?.authUser?.organization_id) throw new Error('Clock workspace changed')
    const deviceTime = new Date().toISOString()
    const payload: Parameters<typeof punchClock>[0] = { kind, request_id: crypto.randomUUID() }
    if (status?.geofence === 'flag' && (kind === 'in' || kind === 'out') && navigator.geolocation) {
      await new Promise<void>((resolve) =>
        navigator.geolocation.getCurrentPosition(
          ({ coords }) => {
            Object.assign(payload, {
              latitude: coords.latitude,
              longitude: coords.longitude,
              accuracy: coords.accuracy,
            })
            resolve()
          },
          () => resolve(),
          { timeout: 7000, maximumAge: 0, enableHighAccuracy: true },
        ),
      )
    }
    // Punches are made for the login the clock was showing; the server refuses them for any other.
    if (owner) Object.assign(payload, { owner_employee: owner.employee, owner_organization: owner.organization })
    const queued: QueuedPunch | null = owner && owner.employee === identity
      ? { ...payload, owner_employee: owner.employee, owner_organization: owner.organization, device_time: deviceTime, punch_offset_seconds: serverOffsets.get(`${owner.employee}:${owner.organization}`) }
      : null
    // Earlier punches on this device go first, including a refused one: nothing later is recorded
    // until the employee has dealt with it. This one waits behind them, in order.
    if (readQueue(identity).length || navigator.onLine === false) {
      if (!queued) throw new Error('offline_unavailable')
      enqueuePunch(identity, queued)
      return 'savedOffline'
    }
    // Reuse the same request ID after a lost response. Never retry a rejected clock transition.
    try {
      await punchClock(payload)
      return 'recorded'
    } catch (error) {
      if (!isConnectionFailure(error)) throw error
      try {
        await punchClock(payload)
        return 'recorded'
      } catch (retryError) {
        if (!isConnectionFailure(retryError) || !queued) throw retryError
        // No connection: keep the punch on this device with its time, and send it later.
        enqueuePunch(identity, queued)
        return 'savedOffline'
      }
    }
  // React Query normally pauses mutations before calling mutationFn when offline. Punches must
  // run locally so their original time is saved; other clock mutations remain online-only.
  }, (result) => result, 'always')
  return { ...mutation, busy }
}

const syncing = new Map<string, Promise<void>>()

/** Errors that mean "not now", not "this punch is wrong": signed out, no access, server trouble. */
function isTemporary(error: unknown) {
  if (isConnectionFailure(error)) return true
  const status = isAxiosError(error) ? error.response?.status ?? 0 : 0
  const code = isAxiosError(error) ? error.response?.data?.code : undefined
  return code === 'offline_identity_mismatch' || ![400, 409].includes(status)
}

/**
 * Sends one person's waiting punches, oldest first, each with the device's time then and now. Stops
 * at the first refusal and keeps that punch visible, so nothing later is recorded out of order.
 *
 * `signedIn` is read before every punch: once it no longer returns this person (sign-out, another
 * account or organisation), the rest stay on the device for them. The server makes the same check
 * for a request already on its way.
 */
export function syncOfflinePunches(employeeId: string, signedIn?: () => string, onRecorded?: () => void, organization?: () => string): Promise<void> {
  const running = syncing.get(employeeId)
  if (running) return running
  const run = (async () => {
    for (;;) {
      const [punch] = readQueue(employeeId)
      if (!punch || punch.refused || !employeeId || signedIn?.() !== employeeId || punch.owner_employee !== employeeId) break
      if (organization && punch.owner_organization !== organization()) break
      try {
        await punchClock({ ...punch, device_now: new Date().toISOString() })
        updateQueue(employeeId, (queue) => queue.filter((item) => item.request_id !== punch.request_id))
        onRecorded?.()
      } catch (error) {
        if (isTemporary(error)) break
        const code = (isAxiosError(error) && error.response?.data?.code) || 'failed'
        updateQueue(employeeId, (queue) => queue.map((item) => (item.request_id === punch.request_id ? refuse(item, code) : item)))
        break
      }
    }
  })().finally(() => {
    syncing.delete(employeeId)
  })
  syncing.set(employeeId, run)
  return run
}

/** Keeps sending waiting punches: now, whenever one is added or the connection returns, and every 30 seconds. */
export function useOfflineSync() {
  const identity = useClockEmployeeId()
  const scope = useClockIdentity()
  const queue = useOfflineQueue(identity)
  const queryClient = useQueryClient()
  const current = useRef(identity)
  current.current = identity
  const currentOrganization = useRef('')
  currentOrganization.current = scope.split(':')[1] || ''
  const waiting = queue.filter((punch) => !punch.refused).length
  useEffect(() => {
    cleanUpOfflinePunches()
    const timer = window.setInterval(() => cleanUpOfflinePunches(), 60 * 60 * 1000)
    return () => window.clearInterval(timer)
  }, [])
  useEffect(() => {
    if (!identity || !waiting) return
    let active = true
    const sync = () => void syncOfflinePunches(identity, () => (active ? current.current : ''), () => {
      void queryClient.invalidateQueries({ queryKey: ['time-clock'] })
    }, () => active ? currentOrganization.current : '')
    sync()
    window.addEventListener('online', sync)
    const timer = window.setInterval(sync, 30000)
    return () => {
      // Signed out or switched: a sync still running for this person stops before its next punch.
      active = false
      window.removeEventListener('online', sync)
      window.clearInterval(timer)
    }
  }, [identity, scope, waiting, queryClient])
  return queue
}
export function useLiveSeconds(status: ClockStatus | undefined, receivedAt: number) {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    if (!status || status.state === 'off') return
    const handle = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(handle)
  }, [status?.state])
  const elapsed = Math.max(0, (now - receivedAt) / 1000)
  return {
    worked: (status?.worked_seconds || 0) + (status?.state === 'working' ? elapsed : 0),
    paused:
      status?.state === 'paused' && status.since
        ? Math.max(0, (Date.parse(status.server_now) - Date.parse(status.since)) / 1000 + elapsed)
        : 0,
  }
}
